// src/components/HeatDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { Card } from "./ui/card";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useHaptics } from "@/haptics/HapticsProvider";
import { toast } from "sonner";
import { ArrowRight, Check, Clock3, LockOpen, Lock, RotateCcw } from "lucide-react";
import { Toggle } from "@/components/ui/toggle";
import PageTutorial from "@/components/tutorial/PageTutorial";
import HelmetIcon from "@/components/HelmetIcon";
import { getHelmetAccentStyle } from "@/lib/helmetAccent";
import { getNativePlatform } from "@/lib/runtime";
import GuestRiderBadge from "@/components/GuestRiderBadge";
import TeamLogo from "@/components/TeamLogo";
import { cn } from "@/lib/utils";
import { preventNativeOnboardingDismiss } from "@/lib/nativeOnboarding";
import { Skeleton } from "@/components/ui/skeleton";
import { isGuestRiderForTeam } from "@/lib/guestRiders";
import {
  buildHeatTimeFromParts,
  formatHeatTimeDisplay,
  isValidHeatTimeInput,
  normalizeHeatTimeInput,
  parseHeatTimeParts,
} from "@/lib/heatTime";
import {
  useAccountPreferences,
  useTutorialStatus,
  useUpdateAccountPreferences,
} from "@/queries/account";


const EMPTY_HEAT = {
  heat_number: 0,
  status: "upcoming",
  riders: {},
  results: [],
};
const deepClone = (x) => JSON.parse(JSON.stringify(x));
const gatesOf = (heatLike) => Object.keys(heatLike?.riders || {}).sort();
const POINTS_MODE_OPTIONS = [
  { value: 1, label: "3p" },
  { value: 2, label: "2p" },
  { value: 3, label: "1p" },
  { value: 4, label: "0p" },
];
const HUNDREDTH_STEP_VALUES = Array.from({ length: 10 }, (_, index) => index * 10);
const HEAT_STATUS_OPTIONS = [
  { value: "completed", label: "OK" },
  { value: "X", label: "X - Utesluten" },
  { value: "M", label: "M - 2-minutersregeln" },
  { value: "N", label: "N - Kom ej till start" },
  { value: "R", label: "R - Stopp" },
  { value: "FX", label: "FX - Fall, utesluten från omstart" },
  { value: "F", label: "F - Fall" },
  { value: "FN", label: "FN - Fall, kom ej till omstart" },
  { value: "TT", label: "TT - Touch the tape" },
];

function normalizeHeatStatus(value) {
  const normalized = String(value || "completed").trim().toUpperCase();
  if (!normalized || normalized === "OK" || normalized === "COMPLETED") return "completed";
  if (normalized === "EXCLUDED") return "X";
  return normalized;
}

function normalizeCompletedPositionsWhenSpecialStatusesExist(resultsByGate) {
  const entries = Object.entries(resultsByGate || {});
  const hasSpecialStatus = entries.some(
    ([, value]) => normalizeHeatStatus(value?.status) !== "completed"
  );
  if (!hasSpecialStatus) return resultsByGate;

  const completedWithPosition = entries
    .filter(([, value]) => normalizeHeatStatus(value?.status) === "completed")
    .map(([gate, value]) => ({
      gate,
      position: Number(value?.position) || 0,
    }))
    .filter((item) => item.position > 0)
    .sort((a, b) => a.position - b.position);

  let changed = false;
  const next = { ...(resultsByGate || {}) };

  completedWithPosition.forEach((item, index) => {
    const expectedPosition = index + 1;
    if (item.position !== expectedPosition) {
      changed = true;
      next[item.gate] = {
        ...next[item.gate],
        position: expectedPosition,
      };
    }
  });

  return changed ? next : resultsByGate;
}

function countSpecialStatuses(resultsByGate, heatLike) {
  return gatesOf(heatLike).reduce((count, gate) => {
    const status = normalizeHeatStatus(resultsByGate?.[gate]?.status);
    return status === "completed" ? count : count + 1;
  }, 0);
}

function persistedResultsByGate(heatLike) {
  const out = {};
  for (const gate of gatesOf(heatLike)) {
    const rid = heatLike.riders[gate]?.rider_id;
    const res = (heatLike.results || []).find(
      (r) => String(r.rider_id) === String(rid)
    );
    out[gate] = {
      position: typeof res?.position === "number" ? res.position : 0,
      status: normalizeHeatStatus(res?.status),
    };
  }
  return out;
}

function isDialogComplete(h, resultsByGate) {
  if (!h) return false;
  const used = new Set();
  for (const gate of gatesOf(h)) {
    const g = resultsByGate[gate] || { position: 0, status: "completed" };
    if (g.status !== "completed") continue;
    const pos = Number(g.position) || 0;
    if (pos < 1 || pos > 4) return false;
    if (used.has(pos)) return false;
    used.add(pos);
  }
  return true;
}

function areResultsUnchanged(liveHeat, resultsByGate) {
  const persisted = persistedResultsByGate(liveHeat);
  const allGates = new Set([...Object.keys(persisted), ...Object.keys(resultsByGate || {})]);
  for (const gate of allGates) {
    const a = persisted[gate] || { position: 0, status: "completed" };
    const b = resultsByGate[gate] || { position: 0, status: "completed" };
    if (Number(a.position) !== Number(b.position)) return false;
    if (String(a.status) !== String(b.status)) return false;
  }
  return true;
}

function isReserveEntry(entry) {
  const lineupNo = Number(entry?.lineup_no ?? 0);
  return Boolean(entry?.is_reserve) || lineupNo === 6 || lineupNo === 7;
}

function isProtocolComplete(matchLike) {
  if (!Array.isArray(matchLike?.heats) || matchLike.heats.length < 15) return false;
  return matchLike.heats.every(
    (heatItem) =>
      heatItem?.status === "completed" && Array.isArray(heatItem?.results) && heatItem.results.length >= 4
  );
}

function snapHundredthsToStep(value) {
  const numeric = Number(value) || 0;
  return Math.max(0, Math.min(90, Math.round(numeric / 10) * 10));
}

function TimeWheelColumn({
  label,
  values,
  selectedIndex,
  onChange,
  onStep,
  formatValue = (value) => String(value),
}) {
  const ITEM_HEIGHT = 36;
  const containerRef = React.useRef(null);
  const scrollTimeoutRef = React.useRef(null);
  const lastSteppedIndexRef = React.useRef(selectedIndex);

  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    node.scrollTop = selectedIndex * ITEM_HEIGHT;
  }, [selectedIndex]);

  React.useEffect(() => {
    lastSteppedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  React.useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const commitNearestValue = React.useCallback(() => {
    const node = containerRef.current;
    if (!node) return;
    const index = Math.max(0, Math.min(values.length - 1, Math.round(node.scrollTop / ITEM_HEIGHT)));
    node.scrollTo({ top: index * ITEM_HEIGHT, behavior: "smooth" });
    onChange(values[index]);
  }, [onChange, values]);

  const handleScroll = React.useCallback(() => {
    const node = containerRef.current;
    if (node) {
      const index = Math.max(0, Math.min(values.length - 1, Math.round(node.scrollTop / ITEM_HEIGHT)));
      if (index !== lastSteppedIndexRef.current) {
        lastSteppedIndexRef.current = index;
        onStep?.(values[index], index);
      }
    }
    if (scrollTimeoutRef.current) {
      window.clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = window.setTimeout(commitNearestValue, 80);
  }, [commitNearestValue, onStep, values]);

  return (
    <div className="native-v3-time-wheel-column flex min-w-0 flex-1 flex-col items-center gap-2">
      <div className="native-v3-time-wheel-label text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </div>
      <div className="native-v3-time-wheel-surface relative w-full overflow-hidden border border-border/70 bg-muted/20">
        <div className="native-v3-time-wheel-focus pointer-events-none absolute inset-x-0 top-1/2 z-10 h-9 -translate-y-1/2 border border-sky-300/70 bg-sky-500/10 dark:border-sky-400/30 dark:bg-sky-400/10" />
        <div
          ref={containerRef}
          onScroll={handleScroll}
          style={{ touchAction: "pan-y", WebkitOverflowScrolling: "touch" }}
          className="native-v3-time-wheel-list h-44 snap-y snap-mandatory overflow-y-auto overscroll-contain px-1 py-[72px] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {values.map((value, index) => {
            const isSelected = index === selectedIndex;
            return (
              <div
                key={`${label}-${value}`}
                onClick={() => onChange(value)}
                className={cn(
                  "native-v3-time-wheel-item flex h-9 w-full snap-center items-center justify-center text-sm font-semibold transition-colors",
                  isSelected ? "text-sky-900 dark:text-sky-100" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {formatValue(value)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function HeatDialog({
  open,
  onOpenChange,
  match,
  heat,
  ridersByTeam,
  canUseTactical,
  tacticalWindow,
  heatSwapGuard,
  onSave, // async ({ heat_number, assignments, results, rr })
}) {
  const isNativeAndroid = getNativePlatform() === "android";
  const hap = useHaptics();
  const { data: preferenceData, isLoading: preferencesLoading } = useAccountPreferences();
  const { data: tutorialData, isLoading: tutorialLoading } = useTutorialStatus();
  const updatePreferences = useUpdateAccountPreferences();
  const [showInputModePrompt, setShowInputModePrompt] = useState(false);
  const [pendingSaveConfirm, setPendingSaveConfirm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const safeHeat = heat ?? EMPTY_HEAT;
  const resultOptions = POINTS_MODE_OPTIONS;

  const liveHeat = useMemo(() => {
    if (!match || !safeHeat?.heat_number) return safeHeat;
    const found = match.heats?.find((x) => x.heat_number === safeHeat.heat_number);
    return found || safeHeat;
  }, [match, safeHeat]);

  const [draftHeat, setDraftHeat] = useState(null);
  const [resultsByGate, setResultsByGate] = useState({});
  const [heatTimeInput, setHeatTimeInput] = useState("");
  const [heatTimeEnabled, setHeatTimeEnabled] = useState(false);
  const [heatTimeMinutes, setHeatTimeMinutes] = useState(0);
  const [heatTimeSeconds, setHeatTimeSeconds] = useState(0);
  const [heatTimeHundredths, setHeatTimeHundredths] = useState(0);
  const [heatTimePickerOpen, setHeatTimePickerOpen] = useState(false);
  const [pickerHeatTimeEnabled, setPickerHeatTimeEnabled] = useState(false);
  const [pickerHeatTimeMinutes, setPickerHeatTimeMinutes] = useState(0);
  const [pickerHeatTimeSeconds, setPickerHeatTimeSeconds] = useState(0);
  const [pickerHeatTimeHundredths, setPickerHeatTimeHundredths] = useState(0);
  const [pickerPreviewEnabled, setPickerPreviewEnabled] = useState(false);
  const [pickerPreviewMinutes, setPickerPreviewMinutes] = useState(0);
  const [pickerPreviewSeconds, setPickerPreviewSeconds] = useState(0);
  const [pickerPreviewHundredths, setPickerPreviewHundredths] = useState(0);
  const [heatTimeNudgeVisible, setHeatTimeNudgeVisible] = useState(false);
  const [rrActive, setRrActive] = useState(false); // <-- RR toggle
  const [manualOverrideConfirmed, setManualOverrideConfirmed] = useState(false);
  const [manualUnlockSnapshot, setManualUnlockSnapshot] = useState(null);

  useEffect(() => {
    if (!open || !liveHeat?.heat_number) return;
    const fresh = deepClone(liveHeat);
    const timeParts = parseHeatTimeParts(fresh?.heat_time || "");
    setDraftHeat(fresh);
    setResultsByGate(persistedResultsByGate(fresh));
    setHeatTimeInput(formatHeatTimeDisplay(fresh?.heat_time || ""));
    setHeatTimeEnabled(Boolean(timeParts.hasValue));
    setHeatTimeMinutes(timeParts.minutes || 0);
    setHeatTimeSeconds(timeParts.seconds || 0);
    setHeatTimeHundredths(snapHundredthsToStep(timeParts.hundredths || 0));
    setPickerHeatTimeEnabled(Boolean(timeParts.hasValue));
    setPickerHeatTimeMinutes(timeParts.minutes || 0);
    setPickerHeatTimeSeconds(timeParts.seconds || 0);
    setPickerHeatTimeHundredths(snapHundredthsToStep(timeParts.hundredths || 0));
    setPickerPreviewEnabled(Boolean(timeParts.hasValue));
    setPickerPreviewMinutes(timeParts.minutes || 0);
    setPickerPreviewSeconds(timeParts.seconds || 0);
    setPickerPreviewHundredths(snapHundredthsToStep(timeParts.hundredths || 0));
    setHeatTimePickerOpen(false);
    setRrActive(false); // nollställ RR vid öppning
    setManualOverrideConfirmed(false);
    setManualUnlockSnapshot(null);
  }, [open, liveHeat?.heat_number]);

  useEffect(() => {
    if (!open) {
      setPendingSaveConfirm(null);
      setIsSaving(false);
      setHeatTimeInput("");
      setHeatTimeEnabled(false);
      setHeatTimeMinutes(0);
      setHeatTimeSeconds(0);
      setHeatTimeHundredths(0);
      setHeatTimePickerOpen(false);
      setPickerHeatTimeEnabled(false);
      setPickerHeatTimeMinutes(0);
      setPickerHeatTimeSeconds(0);
      setPickerHeatTimeHundredths(0);
      setPickerPreviewEnabled(false);
      setPickerPreviewMinutes(0);
      setPickerPreviewSeconds(0);
      setPickerPreviewHundredths(0);
      setManualOverrideConfirmed(false);
      setManualUnlockSnapshot(null);
    }
  }, [open]);

  const ridersChanged = useMemo(() => {
    const h = draftHeat || liveHeat;
    for (const gate of gatesOf(liveHeat)) {
      const beforeId = String(liveHeat.riders[gate]?.rider_id ?? "");
      const afterId = String(h.riders[gate]?.rider_id ?? "");
      if (beforeId !== afterId) return true;
    }
    return false;
  }, [draftHeat, liveHeat]);

  useEffect(() => {
    if (!manualOverrideConfirmed) return;
    if (ridersChanged) return;
    setManualOverrideConfirmed(false);
  }, [manualOverrideConfirmed, ridersChanged]);

  const resultsUnchanged = useMemo(
    () => areResultsUnchanged(liveHeat, resultsByGate),
    [liveHeat, resultsByGate]
  );

  const dialogComplete = useMemo(
    () => isDialogComplete(draftHeat || liveHeat, resultsByGate),
    [draftHeat, liveHeat, resultsByGate]
  );
  const shouldHighlightHeatTime = dialogComplete && !heatTimeEnabled;

  useEffect(() => {
    if (!shouldHighlightHeatTime) {
      setHeatTimeNudgeVisible(false);
      return undefined;
    }
    setHeatTimeNudgeVisible(true);
    const timeoutId = window.setTimeout(() => {
      setHeatTimeNudgeVisible(false);
    }, 1200);
    return () => window.clearTimeout(timeoutId);
  }, [shouldHighlightHeatTime]);

  const currentHeat = draftHeat || liveHeat;
  const normalizedHeatTime = useMemo(() => normalizeHeatTimeInput(heatTimeInput), [heatTimeInput]);
  const heatTimeHasError = useMemo(() => !isValidHeatTimeInput(heatTimeInput), [heatTimeInput]);
  const pickerHeatTimeValue = useMemo(
    () =>
      pickerHeatTimeEnabled
        ? buildHeatTimeFromParts({
            hasValue: true,
            minutes: pickerHeatTimeMinutes,
            seconds: pickerHeatTimeSeconds,
            hundredths: pickerHeatTimeHundredths,
          })
        : "",
    [pickerHeatTimeEnabled, pickerHeatTimeHundredths, pickerHeatTimeMinutes, pickerHeatTimeSeconds]
  );
  const pickerPreviewTimeValue = useMemo(
    () =>
      buildHeatTimeFromParts({
        hasValue: true,
        minutes: pickerPreviewMinutes,
        seconds: pickerPreviewSeconds,
        hundredths: pickerPreviewHundredths,
      }),
    [
      pickerPreviewHundredths,
      pickerPreviewMinutes,
      pickerPreviewSeconds,
    ]
  );
  const persistedHeatTime = useMemo(
    () => formatHeatTimeDisplay(liveHeat?.heat_time || ""),
    [liveHeat?.heat_time]
  );
  const heatTimeChanged = useMemo(
    () => String(normalizedHeatTime || "") !== String(persistedHeatTime || ""),
    [normalizedHeatTime, persistedHeatTime]
  );
  const nothingChanged = !ridersChanged && resultsUnchanged && !heatTimeChanged;
  const effectiveManualOverride = rrActive || manualOverrideConfirmed;
  const manualUnlockHasLineupChanges = rrActive && ridersChanged;
  const canConfirmManualUnlockChanges = rrActive && ridersChanged;
  const specialStatusCount = useMemo(
    () => countSpecialStatuses(resultsByGate, currentHeat),
    [currentHeat, resultsByGate]
  );
  const maxCompletedPosition = Math.max(0, 4 - specialStatusCount);

  const lineupRidersByTeam = useMemo(() => {
    const fallbackByTeam = ridersByTeam || {};

    const buildForTeam = (teamKey) => {
      const lineupGroups = [
        match?.lineups?.[teamKey] || [],
        match?.initial_lineups?.[teamKey] || [],
        match?.source_lineups?.[teamKey] || [],
      ];
      const fallbackRoster = fallbackByTeam[teamKey] || [];
      const fallbackRosterIds = new Set(
        fallbackRoster
          .map((rider) => String(rider?.id || ""))
          .filter(Boolean)
      );
      const byId = new Map();

      fallbackRoster.forEach((rider) => {
        const riderId = String(rider?.id || rider?.rider_id || "");
        if (!riderId) return;
        byId.set(riderId, {
          ...rider,
          id: riderId,
          team_id: rider?.team_id || "",
          club_team_id: rider?.club_team_id || rider?.team_id || "",
          guest_active: Boolean(rider?.guest_active),
          guest_for_team_id: rider?.guest_for_team_id || "",
          guest_note: rider?.guest_note || "",
          is_guest_for_team: Boolean(rider?.is_guest_for_team),
        });
      });

      lineupGroups.forEach((lineup, groupIndex) => {
        lineup.forEach((item) => {
          const riderId = String(item?.rider_id ?? item?.id ?? "");
          if (!riderId) return;
          const fallback = fallbackRoster.find((r) => String(r?.id) === riderId);
          const existing = byId.get(riderId) || {};
          byId.set(riderId, {
            ...existing,
            id: riderId,
            name: item?.name || item?.rider_name || fallback?.name || existing?.name || riderId,
            lineup_no: Number(item?.lineup_no ?? fallback?.lineup_no ?? existing?.lineup_no ?? 0) || undefined,
            team_id: item?.team_id || fallback?.team_id || existing?.team_id || "",
            club_team_id: item?.club_team_id || fallback?.club_team_id || item?.team_id || fallback?.team_id || existing?.club_team_id || existing?.team_id || "",
            guest_active:
              typeof item?.guest_active === "boolean"
                ? item.guest_active
                : Boolean(fallback?.guest_active),
            guest_for_team_id: item?.guest_for_team_id || fallback?.guest_for_team_id || "",
            guest_note: item?.guest_note || fallback?.guest_note || "",
            is_guest_for_team:
              typeof item?.is_guest_for_team === "boolean"
                ? item.is_guest_for_team
                : typeof fallback?.is_guest_for_team === "boolean"
                  ? fallback.is_guest_for_team
                  : false,
          });
        });
      });

      return Array.from(byId.values()).sort(
        (a, b) => Number(a?.lineup_no ?? 99) - Number(b?.lineup_no ?? 99)
      );
    };

    return {
      home: buildForTeam("home"),
      away: buildForTeam("away"),
    };
  }, [match?.initial_lineups, match?.lineups, match?.source_lineups, ridersByTeam]);

  const lineupBaselineByGate = useMemo(() => {
    const out = {};
    const lineupIdSets = {
      home: new Set((lineupRidersByTeam?.home || []).map((item) => String(item.id))),
      away: new Set((lineupRidersByTeam?.away || []).map((item) => String(item.id))),
    };

    for (const gate of gatesOf(currentHeat)) {
      const entry = currentHeat?.riders?.[gate];
      if (!entry) continue;

      const lineupNo = Number(entry?.lineup_no ?? 0);
      const team = entry?.team;
      if (!lineupNo || !team) continue;

      const lineupEntry = (lineupRidersByTeam?.[team] || []).find(
        (item) => Number(item?.lineup_no ?? 0) === lineupNo
      );
      if (!lineupEntry?.id) continue;

      const currentRiderId = String(entry?.rider_id ?? "");
      out[gate] = {
        riderId: String(lineupEntry.id),
        name: lineupEntry.name || "Ursprungsförare",
        currentRiderStillInLineup: lineupIdSets[team]?.has(currentRiderId),
      };
    }

    return out;
  }, [currentHeat, lineupRidersByTeam]);

  const protocolComplete = useMemo(() => isProtocolComplete(match), [match]);

  const riderHeatStatusById = useMemo(() => {
    if (!match?.heats?.length) return {};

    const lineups = match?.lineups || {};
    const lineupMeta = {};
    for (const side of ["home", "away"]) {
      for (const rider of lineups?.[side] || []) {
        const rid = String(rider?.rider_id ?? rider?.id ?? "");
        if (!rid) continue;
        const lineupNo = Number(rider?.lineup_no ?? 0);
        lineupMeta[rid] = {
          isReserve: lineupNo === 6 || lineupNo === 7,
        };
      }
    }

    const rideLimits = match?.meta?.rules?.ride_limits || {};
    const activeHeats = match.heats.map((item) =>
      item?.heat_number === currentHeat?.heat_number ? currentHeat : item
    );

    const counts = {};
    for (const heatItem of activeHeats) {
      for (const gate of gatesOf(heatItem)) {
        const riderId = String(heatItem?.riders?.[gate]?.rider_id ?? "");
        if (!riderId) continue;
        counts[riderId] = (counts[riderId] || 0) + 1;
      }
    }

    const out = {};
    Object.entries(counts).forEach(([riderId, count]) => {
      const isReserve = Boolean(lineupMeta[riderId]?.isReserve);
      const max = rrActive
        ? isReserve
          ? Number(rideLimits?.rr_reserve_max ?? 7)
          : Number(rideLimits?.rr_main_max ?? 7)
        : effectiveManualOverride
        ? isReserve
          ? Number(rideLimits?.rr_reserve_max ?? 7)
          : Number(rideLimits?.rr_main_max ?? 7)
        : isReserve
        ? Number(rideLimits?.reserve_max ?? 6)
        : Number(rideLimits?.main_max ?? 6);

      out[riderId] = { count, max, isReserve };
    });

    return out;
  }, [match, currentHeat, rrActive, effectiveManualOverride]);

  const teammateIdsInHeat = (h, team, excludeGate) => {
    const ids = [];
    for (const g of gatesOf(h)) {
      if (g === excludeGate) continue;
      const e = h.riders[g];
      if (e?.team === team && e?.rider_id) {
        ids.push(String(e.rider_id));
      }
    }
    return ids;
  };

  const setDraftRider = (gate, newRiderId) => {
    setDraftHeat((prev) => {
      const base = prev ?? liveHeat;
      const next = deepClone(base);

      const entry = next.riders?.[gate];
      if (!entry) return prev;

      // I RR-läge ignorerar vi låsta reserver och taktiska guards i UI
      if (!effectiveManualOverride && entry.locked) {
        toast.info("Den här platsen är låst för reservernas schemalagda heat.");
        try { hap.warning?.(); } catch { }
        return prev;
      }

      const team = entry.team;
      if (!team) return prev;

      const forbiddenIds = teammateIdsInHeat(next, team, gate);
      if (forbiddenIds.includes(String(newRiderId))) {
        toast.error("Du kan inte välja samma förare mer än en gång i samma heat.");
        try { hap.error?.(); } catch { }
        return prev;
      }

      if (!effectiveManualOverride) {
        const tempRiders = deepClone(next.riders);
        tempRiders[gate].rider_id = String(newRiderId);
        if (heatSwapGuard && !heatSwapGuard(liveHeat, tempRiders)) {
          toast.error("Endast en taktisk reserv kan användas per heat.");
          try { hap.error?.(); } catch { }
          return prev;
        }
      }

      const roster = lineupRidersByTeam?.[team] || [];
      const selected = roster.find((r) => String(r.id) === String(newRiderId));

      next.riders[gate].rider_id = String(newRiderId);
      next.riders[gate].name = selected?.name ?? next.riders[gate].name;

      try { hap.selection?.(); } catch { }
      return next;
    });
  };

  const restoreOriginalRider = (gate) => {
    const originalRiderId = lineupBaselineByGate?.[gate]?.riderId;
    if (!originalRiderId) return;
    setDraftHeat((prev) => {
      const base = prev ?? liveHeat;
      const next = deepClone(base);
      const entry = next.riders?.[gate];
      if (!entry) return prev;

      const team = entry.team;
      const roster = lineupRidersByTeam?.[team] || [];
      const selected = roster.find((r) => String(r.id) === String(originalRiderId));

      next.riders[gate].rider_id = String(originalRiderId);
      next.riders[gate].name = selected?.name ?? next.riders[gate].name;

      try { hap.selection?.(); } catch { }
      return next;
    });
  };

  const setPositionExclusiveForGate = (gate, pos) => {
    setResultsByGate((s) => {
      const current = s[gate] || { position: 0, status: "completed" };
      const alreadySelected = Number(current.position) === pos;

      if (alreadySelected) {
        return {
          ...s,
          [gate]: { ...current, position: 0 },
        };
      }

      const next = { ...s };
      const currentPosition = current.status === "completed" ? Number(current.position) || 0 : 0;
      let conflictingGate = null;

      for (const g of Object.keys(next)) {
        if (g === gate) continue;
        const og = next[g] || { position: 0, status: "completed" };
        if (og.status === "completed" && Number(og.position) === pos) {
          conflictingGate = g;
          break;
        }
      }

      const mine = next[gate] || { position: 0, status: "completed" };
      next[gate] = { ...mine, position: mine.status === "completed" ? pos : 0 };

      if (conflictingGate) {
        const other = next[conflictingGate] || { position: 0, status: "completed" };
        next[conflictingGate] = {
          ...other,
          position:
            other.status === "completed" && currentPosition <= maxCompletedPosition
              ? currentPosition
              : 0,
        };
      }

      try { hap.tap?.(); } catch { }
      return next;
    });
  };

  const setStatusForGate = (gate, newStatus) => {
    setResultsByGate((s) => {
      const cur = s[gate] || { position: 0, status: "completed" };
      const next = {
        ...s,
        [gate]: {
          ...cur,
          status: newStatus,
          position: newStatus === "completed" ? cur.position : 0,
        },
      };
      try { hap.selection?.(); } catch { }
      return newStatus === "completed"
        ? next
        : normalizeCompletedPositionsWhenSpecialStatusesExist(next);
    });
  };

  const handleToggleManualUnlock = (pressed) => {
    if (pressed) {
      hap.selection();
      setManualUnlockSnapshot({
        heat: deepClone(draftHeat || liveHeat),
        manualOverrideConfirmed,
      });
      setRrActive(true);
      return;
    }

    if (ridersChanged) {
      try { hap.warning?.(); } catch { }
      toast.info("Bekräfta eller avbryt förarändringen innan du låser igen.");
      return;
    }

    hap.selection();
    setManualUnlockSnapshot(null);
    setRrActive(false);
    setManualOverrideConfirmed(false);
  };

  const handleCancelManualUnlockChanges = () => {
    const snapshotHeat = manualUnlockSnapshot?.heat ? deepClone(manualUnlockSnapshot.heat) : deepClone(liveHeat);
    setDraftHeat(snapshotHeat);
    setManualOverrideConfirmed(Boolean(manualUnlockSnapshot?.manualOverrideConfirmed));
    setManualUnlockSnapshot(null);
    setRrActive(false);
    try { hap.selection?.(); } catch { }
  };

  const handleConfirmManualUnlockChanges = () => {
    if (!ridersChanged) return;
    setManualUnlockSnapshot(null);
    setRrActive(false);
    setManualOverrideConfirmed(true);
    try { hap.selection?.(); } catch { }
    toast.success("Ändringar bekräftade.");
  };

  const getAssignmentChanges = () => {
    const h = draftHeat || liveHeat;

    return gatesOf(liveHeat).flatMap((gate) => {
      const beforeId = String(liveHeat.riders[gate]?.rider_id ?? "");
      const afterId = String(h.riders[gate]?.rider_id ?? "");
      if (beforeId === afterId) return [];

      return [{
        gate,
        fromName: liveHeat.riders[gate]?.name || "Ursprungsförare",
        toName: h.riders[gate]?.name || "Ny förare",
        toId: afterId,
      }];
    });
  };

  const requestSave = () => {
    if (isSaving) return;
    const assignmentChanges = getAssignmentChanges();
    if (assignmentChanges.length) {
      setPendingSaveConfirm(assignmentChanges);
      return;
    }
    handleSave();
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (heatTimeHasError) {
      toast.error("Ange en giltig heattid i formatet 58.43 eller 1:02.11.");
      return;
    }
    const h = draftHeat || liveHeat;

    const assignments = Object.fromEntries(
      getAssignmentChanges().map((change) => [change.gate, change.toId])
    );

    const results = gatesOf(h).map((gate) => {
      const rid = String(h.riders[gate]?.rider_id ?? "");
      const g = resultsByGate[gate] || {};
      const position = Number(g.position) || 0;
      const status = normalizeHeatStatus(g.status);
      return { rider_id: rid, position, status };
    });

    try {
      setIsSaving(true);
      await onSave({
        heat_number: liveHeat.heat_number,
        assignments,
        results,
        rr: effectiveManualOverride,
        heat_time: normalizedHeatTime || "",
      });
      try {
        // hap.success?.(); 
      } catch {

      }
      onOpenChange(false);
    } catch (e) {
      try { hap.error?.(); } catch { }
      const msg =
        e?.message ||
        e?.detail ||
        "Kunde inte spara heatet. Kontrollera byten/regler och försök igen.";
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChooseInputMode = async () => {
    try {
      await updatePreferences.mutateAsync({
        preferences: {
          heat_result_input_mode: mode,
          heat_result_input_mode_confirmed: true,
          auto_scroll_next_heat:
            preferenceData?.preferences?.auto_scroll_next_heat !== false,
          allow_haptics: preferenceData?.preferences?.allow_haptics !== false,
          haptic_intensity: preferenceData?.preferences?.haptic_intensity || "normal",
          protocol_default_view:
            preferenceData?.preferences?.protocol_default_view || "cards",
          protocol_default_view_confirmed:
            preferenceData?.preferences?.protocol_default_view_confirmed || false,
        },
      });
      setShowInputModePrompt(false);
      toast.success(
        mode === "points"
          ? "Poänginmatning aktiverad."
          : "Standardläget är valt."
      );
    } catch (error) {
      toast.error(error?.message || "Kunde inte spara valet.");
    }
  };

  const tacticalEligibleTeams = useMemo(() => {
    if (
      protocolComplete ||
      !currentHeat ||
      !canUseTactical ||
      typeof canUseTactical !== "function"
    ) {
      return [];
    }
    const teamsInHeat = Array.from(
      new Set(gatesOf(currentHeat).map((gate) => currentHeat.riders[gate]?.team).filter(Boolean))
    );
    return teamsInHeat
      .filter((team) => canUseTactical(team))
      .map((team) => ({
        key: team,
        team,
        name: team === "home" ? match?.home_team || "Hemmalag" : match?.away_team || "Bortalag",
      }));
  }, [protocolComplete, currentHeat, canUseTactical, match?.home_team, match?.away_team]);

  const openHeatTimePicker = () => {
    const timeParts = parseHeatTimeParts(heatTimeInput || liveHeat?.heat_time || "");
    setPickerHeatTimeEnabled(Boolean(timeParts.hasValue));
    setPickerHeatTimeMinutes(timeParts.minutes || 0);
    setPickerHeatTimeSeconds(timeParts.seconds || 0);
    setPickerHeatTimeHundredths(snapHundredthsToStep(timeParts.hundredths || 0));
    setPickerPreviewEnabled(Boolean(timeParts.hasValue));
    setPickerPreviewMinutes(timeParts.minutes || 0);
    setPickerPreviewSeconds(timeParts.seconds || 0);
    setPickerPreviewHundredths(snapHundredthsToStep(timeParts.hundredths || 0));
    setHeatTimePickerOpen(true);
  };

  const applyHeatTimeSelection = () => {
    const nextValue = pickerHeatTimeEnabled ? pickerHeatTimeValue : "";
    const normalized = formatHeatTimeDisplay(nextValue);
    setHeatTimeEnabled(Boolean(pickerHeatTimeEnabled));
    setHeatTimeMinutes(pickerHeatTimeMinutes);
    setHeatTimeSeconds(pickerHeatTimeSeconds);
    setHeatTimeHundredths(snapHundredthsToStep(pickerHeatTimeHundredths));
    setHeatTimeInput(normalized);
    setHeatTimePickerOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* <DialogContent className="max-w-2xl top-1 md:top-10 translate-y-0 max-h-[100vh] overflow-y-auto"> */}
      <DialogContent
        animation="zoom"
        onPointerDownOutside={preventNativeOnboardingDismiss}
        onInteractOutside={preventNativeOnboardingDismiss}
        className="native-v3-heat-dialog top-[calc(env(safe-area-inset-top)+0.5rem)] w-[calc(100vw-1rem)] max-w-2xl translate-x-[-50%] translate-y-0 gap-2 max-h-[calc(100dvh-env(safe-area-inset-top)-0.5rem)] overflow-y-auto p-4 [&>button]:top-4 sm:top-[50%] sm:w-full sm:translate-y-[-50%] sm:max-h-[100vh] sm:p-6 sm:[&>button]:top-4"
      >
        {open && (
          <PageTutorial
            pageName="heatDialog"
            autoStart={open && !preferencesLoading && !tutorialLoading}
          />
        )}
        <AlertDialog
          open={Boolean(pendingSaveConfirm)}
          onOpenChange={(isOpen) => {
            if (!isOpen) setPendingSaveConfirm(null);
          }}
        >
          <AlertDialogContent className="native-v3-protocol-alert native-v3-heat-save-alert w-[calc(100vw-1rem)] max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>Spara med förarbyte?</AlertDialogTitle>
              <AlertDialogDescription>
                Vill du spara heatet med följande ändring{pendingSaveConfirm?.length > 1 ? "ar" : ""}?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2 text-sm">
              {(pendingSaveConfirm || []).map((change) => (
                <div key={change.gate} className="native-v3-heat-change-row rounded-lg border bg-muted/40 px-3 py-2">
                  <div className="font-medium">Bana {change.gate}</div>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <span>{change.fromName}</span>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span>{change.toName}</span>
                  </div>
                </div>
              ))}
            </div>
            <AlertDialogFooter className="native-v3-protocol-alert-footer">
              <AlertDialogCancel className="native-v3-protocol-alert-cancel">Avbryt</AlertDialogCancel>
              <AlertDialogAction
                className="native-v3-protocol-alert-primary"
                disabled={isSaving}
                onClick={() => {
                  setPendingSaveConfirm(null);
                  handleSave();
                }}
              >
                {isSaving ? "Sparar..." : "Spara resultat"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Dialog open={heatTimePickerOpen} onOpenChange={setHeatTimePickerOpen}>
          <DialogContent
            animation="zoom"
            className="native-v3-heat-time-dialog w-[calc(100vw-1.5rem)] max-w-[360px] gap-3 border-border bg-background p-4 shadow-2xl"
          >
            <DialogHeader className="native-v3-heat-time-header items-center text-center">
              <div className="native-v3-heat-time-kicker inline-flex items-center gap-2 bg-sky-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-800 dark:text-sky-100">
                <Clock3 className="h-3.5 w-3.5" />
                Heattid
              </div>
              <DialogTitle
                className={cn(
                  "text-4xl font-black tracking-tight text-foreground transition-all duration-300",
                  heatTimePickerOpen ? "scale-100 opacity-100" : "scale-95 opacity-0"
                )}
              >
                {formatHeatTimeDisplay(pickerPreviewTimeValue)}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Dra upp eller ned i hjulen för att ställa in heatets tid.
              </DialogDescription>
            </DialogHeader>

            <div className="native-v3-time-wheels mx-auto grid w-full max-w-[280px] grid-cols-3 gap-1.5">
              <TimeWheelColumn
                label="Min"
                values={[0, 1, 2]}
                selectedIndex={Math.max(0, Math.min(2, pickerHeatTimeMinutes))}
                onChange={(value) => {
                  setPickerHeatTimeEnabled(true);
                  setPickerHeatTimeMinutes(value);
                  setPickerPreviewEnabled(true);
                  setPickerPreviewMinutes(value);
                }}
                onStep={(value) => {
                  setPickerPreviewEnabled(true);
                  setPickerPreviewMinutes(value);
                  try { hap.custom?.(6); } catch {}
                }}
                formatValue={(value) => String(value)}
              />
              <TimeWheelColumn
                label="Sek"
                values={Array.from({ length: 60 }, (_, index) => index)}
                selectedIndex={Math.max(0, Math.min(59, pickerHeatTimeSeconds))}
                onChange={(value) => {
                  setPickerHeatTimeEnabled(true);
                  setPickerHeatTimeSeconds(value);
                  setPickerPreviewEnabled(true);
                  setPickerPreviewSeconds(value);
                }}
                onStep={(value) => {
                  setPickerPreviewEnabled(true);
                  setPickerPreviewSeconds(value);
                  try { hap.custom?.(6); } catch {}
                }}
                formatValue={(value) => String(value).padStart(2, "0")}
              />
              <TimeWheelColumn
                label="Hund"
                values={HUNDREDTH_STEP_VALUES}
                selectedIndex={Math.max(
                  0,
                  HUNDREDTH_STEP_VALUES.findIndex((value) => value === snapHundredthsToStep(pickerHeatTimeHundredths))
                )}
                onChange={(value) => {
                  setPickerHeatTimeEnabled(true);
                  setPickerHeatTimeHundredths(value);
                  setPickerPreviewEnabled(true);
                  setPickerPreviewHundredths(value);
                }}
                onStep={(value) => {
                  setPickerPreviewEnabled(true);
                  setPickerPreviewHundredths(value);
                  try { hap.custom?.(6); } catch {}
                }}
                formatValue={(value) => String(value).padStart(2, "0")}
              />
            </div>

            <DialogFooter className="native-v3-heat-time-footer gap-2 sm:justify-between sm:space-x-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPickerHeatTimeEnabled(false);
                  setPickerHeatTimeMinutes(0);
                  setPickerHeatTimeSeconds(0);
                  setPickerHeatTimeHundredths(0);
                  setPickerPreviewEnabled(false);
                  setPickerPreviewMinutes(0);
                  setPickerPreviewSeconds(0);
                  setPickerPreviewHundredths(0);
                }}
              >
                Rensa
              </Button>
              <Button className="native-v3-heat-time-confirm" type="button" size="sm" onClick={applyHeatTimeSelection}>
                Klar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <DialogHeader className="native-v3-heat-dialog-header">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <DialogTitle className="min-w-0">
                {liveHeat.status === "completed" ? "Redigera" : "Registrera"} Heat{" "}
                {liveHeat.heat_number} Resultat
              </DialogTitle>
            </div>
          </div>
          <DialogDescription>
            <div className="native-v3-heat-time-trigger-row flex items-center justify-center text-center">
              <button
                type="button"
                onClick={openHeatTimePicker}
                className={cn(
                  "native-v3-heat-time-trigger inline-flex items-center justify-center gap-1 px-2 py-1 text-sky-700 transition-all duration-200 hover:bg-sky-500/12 hover:text-sky-800 dark:text-sky-300 dark:hover:bg-sky-500/12 dark:hover:text-sky-100",
                  heatTimeNudgeVisible &&
                    "animate-[pulse_1.2s_ease-out_1] bg-sky-500/10 ring-1 ring-sky-400/60 dark:bg-sky-400/10 dark:ring-sky-300/40"
                )}
                aria-label={heatTimeEnabled ? "Ändra heattid" : "Lägg till heattid"}
                title={heatTimeEnabled ? "Ändra heattid" : "Lägg till heattid"}
              >
                <Clock3 className="h-4 w-4" />
                <span className="text-sm font-semibold text-sky-700 dark:text-sky-300">
                  {heatTimeEnabled ? formatHeatTimeDisplay(heatTimeInput) : "Heattid"}
                </span>
              </button>
            </div>
            {/* <span className="inline-flex items-center gap-1.5">
              <Medal className="h-4 w-4 shrink-0" aria-hidden />
              <span>Poängläge är aktivt.</span>
            </span> */}
          </DialogDescription>
        </DialogHeader>

        {/* RR-toggle */}
        <div className="native-v3-heat-dialog-tools space-y-2" id="heatDialogTools">
          <Toggle
            pressed={rrActive}
            onPressedChange={handleToggleManualUnlock}
            className={cn(
              "flex h-auto w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left transition-colors",
              "bg-background hover:bg-background",
              "data-[state=off]:border-border data-[state=off]:text-foreground",
              "data-[state=on]:border-amber-300 data-[state=on]:bg-amber-50 data-[state=on]:text-amber-950 data-[state=on]:shadow-sm",
              "dark:data-[state=on]:border-amber-400/40 dark:data-[state=on]:bg-amber-500/10 dark:data-[state=on]:text-amber-100"
            )}
          >
            <div className="flex items-center gap-2">
              {rrActive ? (
                <LockOpen className="h-4 w-4" />
              ) : (
                <Lock className="h-4 w-4" />
              )}
              <div className="space-y-0.5">
                <div className="text-sm font-medium">
                  {rrActive ? "Upplåst för manuella ändringar" : "Lås upp för manuella ändringar"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {manualUnlockHasLineupChanges
                    ? "Bekräfta eller avbryt pågående ändringar."
                    : "Använd vid R.R., T.R. eller andra manuella byten."}
                </div>
              </div>
            </div>
          </Toggle>

          {manualOverrideConfirmed || tacticalEligibleTeams.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {manualOverrideConfirmed ? (
                <span className="native-v3-heat-tool-tag inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50/80 px-2.5 py-1 text-[11px] font-medium text-amber-900 dark:border-amber-400/40 dark:bg-amber-500/10 dark:text-amber-100">
                  <span>Manuella ändringar</span>
                  <Check className="h-3.5 w-3.5 shrink-0" />
                </span>
              ) : null}
              {tacticalEligibleTeams.map((item) => (
                <Popover key={item.key}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="native-v3-heat-tool-tag inline-flex items-center gap-2 rounded-full border border-blue-300 bg-blue-500/10 px-2.5 py-1 text-[11px] font-medium text-blue-700 transition-colors dark:border-blue-400/40 dark:bg-blue-500/15 dark:text-blue-200"
                    >
                      <TeamLogo
                        teamName={item.name}
                        size={16}
                        className="h-4 w-4 rounded-full object-contain"
                      />
                      <span className="truncate max-w-[110px]">{item.name}</span>
                      <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white dark:bg-blue-400 dark:text-slate-950">
                        T.R
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="native-v3-heat-popover w-64 border-border bg-card p-3 text-xs text-card-foreground shadow-lg">
                    {item.name} har möjlighet att använda taktisk reserv.
                  </PopoverContent>
                </Popover>
              ))}
            </div>
          ) : null}
        </div>

        {gatesOf(currentHeat).length === 0 ? (
          <div className="native-v3-heat-riders-skeleton" aria-label="Laddar förare">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index}>
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-5 w-2/3" />
                <div><Skeleton className="h-10 flex-1" /><Skeleton className="h-10 flex-1" /></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div
              id="heatDialogContent"
              className="grid grid-cols-1 gap-2 md:grid-cols-2"
            >
              {gatesOf(currentHeat).map((gate) => {
                const entry = currentHeat.riders[gate];
                const team = entry.team;
                const teamName = team === "home" ? match?.home_team || "Hemmalag" : match?.away_team || "Bortalag";
                const isLocked = Boolean(entry.locked);
                const originalRiderId = String(lineupBaselineByGate?.[gate]?.riderId ?? "");
                const currentRiderId = String(entry?.rider_id ?? "");
                const riderChanged =
                  Boolean(originalRiderId) &&
                  currentRiderId !== originalRiderId &&
                  Boolean(lineupBaselineByGate?.[gate]?.currentRiderStillInLineup);
                const tacticalEligible = tacticalEligibleTeams.some(
                  (candidate) => candidate.team === team
                );
                const riderStatus = riderHeatStatusById[currentRiderId] || null;
                const reserveEntry = isReserveEntry(entry);
                const selectedTeamRider = (lineupRidersByTeam?.[team] || []).find(
                  (opt) => String(opt.id) === currentRiderId
                );
                const isNearMax =
                  riderStatus && riderStatus.count === Math.max(1, riderStatus.max - 1);
                const isAtOrOverMax =
                  riderStatus && riderStatus.count >= riderStatus.max;

                const canPick = rrActive;

                const gRes = resultsByGate[gate] || { position: 0, status: "completed" };
                const isSpecialStatus = normalizeHeatStatus(gRes.status) !== "completed";

                const forbiddenIds = teammateIdsInHeat(currentHeat, team, gate);

                return (
                  <Card
                    key={gate}
                    id="heatDialogRiderList"
                    style={isNativeAndroid ? getHelmetAccentStyle(entry.helmet_color) : undefined}
                    className={cn(
                      "native-v3-heat-dialog-rider rounded-lg border p-2 transition-all duration-200",
                      tacticalEligible
                        ? "border-blue-200 bg-blue-50/40 dark:border-blue-400/25 dark:bg-blue-500/5"
                        : "border"
                    )}
                  >
                    <div className="native-v3-heat-rider-heading flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="native-v3-heat-gate-number">{gate}</span>
                        <span className="native-v3-heat-gate-label text-sm text-muted-foreground">Bana</span>
                        <HelmetIcon
                          color={entry.helmet_color}
                          className="size-[22px]"
                          title={`Hjälmfärg ${entry.helmet_color}`}
                        />
                      </div>

                      <div className="native-v3-heat-rider-meta flex items-center gap-2">
                        {riderChanged && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            onClick={() => restoreOriginalRider(gate)}
                            title="Återställ ursprungsförare"
                            aria-label="Återställ ursprungsförare"
                          >
                            <RotateCcw className="size-3.5" />
                          </Button>
                        )}
                        {rrActive ? (
                          <span className="native-v3-heat-meta-pill native-v3-heat-meta-pill-unlocked inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900 ring-1 ring-amber-300 dark:bg-amber-400/15 dark:text-amber-100 dark:ring-amber-400/40">
                            <LockOpen className="size-3" />
                            Upplåst
                          </span>
                        ) : null}

                        {reserveEntry && !rrActive && (
                          <span className="native-v3-heat-meta-pill inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5 bg-muted text-muted-foreground ring-1 ring-border">
                            <Lock className="size-3" />
                            Reserv
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="native-v3-heat-rider-body mt-2 space-y-2">
                      {/* Förare */}
                      <div className="native-v3-heat-rider-selection flex items-center justify-between gap-2">
                        <div
                          data-unlocked={rrActive ? "true" : "false"}
                          className={cn(
                            "native-v3-heat-rider-field min-w-0 rounded-md transition-colors",
                            rrActive && "ring-1 ring-amber-300 bg-amber-50/60 dark:ring-amber-400/40 dark:bg-amber-500/10"
                          )}
                        >
                          {canPick ? (
                            <Select
                              value={String(entry?.rider_id ?? "")}
                              onValueChange={(val) => setDraftRider(gate, val)}
                            >
                              <SelectTrigger className="native-v3-heat-rider-trigger h-8 border-0 bg-transparent px-2 text-sm font-medium shadow-none">
                                <div className="flex min-w-0 items-center gap-2">
                                  <TeamLogo
                                    teamName={teamName}
                                    size={14}
                                    className="h-3.5 w-3.5 shrink-0 object-contain"
                                  />
                                  {(() => {
                                    return selectedTeamRider ? (
                                      <span className="flex min-w-0 items-center gap-2">
                                        <span className="truncate text-sm font-medium">{selectedTeamRider.name}</span>
                                        {isGuestRiderForTeam(selectedTeamRider, team === "home" ? match?.home_team_id : match?.away_team_id) ? (
                                          <GuestRiderBadge iconOnly className="shrink-0 px-1" />
                                        ) : null}
                                      </span>
                                    ) : (
                                      <SelectValue placeholder="Förare" className="text-sm font-medium" />
                                    );
                                  })()}
                                </div>
                              </SelectTrigger>
                              <SelectContent className="native-v3-heat-dialog-select max-h-64 bg-secondary text-sm font-medium">
                                {(lineupRidersByTeam?.[team] || []).map((opt) => {
                                const isForbidden = forbiddenIds.includes(String(opt.id));
                                  return (
                                    <SelectItem
                                      key={opt.id}
                                      value={String(opt.id)}
                                      disabled={isForbidden}
                                      className="text-sm font-medium data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground"
                                    >
                                      <span className="flex min-w-0 items-center gap-2">
                                        <span className="truncate">{opt.name}{isForbidden ? " (i heatet)" : ""}</span>
                                        {isGuestRiderForTeam(opt, team === "home" ? match?.home_team_id : match?.away_team_id) ? (
                                          <GuestRiderBadge className="shrink-0" />
                                        ) : null}
                                      </span>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="native-v3-heat-rider-static h-8 flex items-center gap-2 min-w-0 px-2">
                              <TeamLogo
                                teamName={teamName}
                                size={14}
                                className="h-3.5 w-3.5 shrink-0 object-contain"
                              />
                              <div className="flex min-w-0 items-center gap-2">
                                <div className="truncate text-sm font-medium">{selectedTeamRider?.name || entry.name}</div>
                                {isGuestRiderForTeam(selectedTeamRider || entry, team === "home" ? match?.home_team_id : match?.away_team_id) ? (
                                  <GuestRiderBadge iconOnly className="shrink-0 px-1" />
                                ) : null}
                              </div>
                            </div>
                          )}
                        </div>

                        {(riderChanged || isNearMax || isAtOrOverMax) && (
                          <div className="native-v3-heat-rider-flags flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                            {riderChanged ? (
                              <span className="native-v3-heat-meta-pill native-v3-heat-meta-pill-changed inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-amber-300 dark:bg-amber-400/15 dark:text-amber-100 dark:ring-amber-400/40">
                                Ändrad
                              </span>
                            ) : null}
                            {isAtOrOverMax ? (
                              <span className="native-v3-heat-meta-pill native-v3-heat-meta-pill-warning inline-flex items-center rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-medium text-red-700 ring-1 ring-red-300 dark:bg-red-500/15 dark:text-red-100 dark:ring-red-400/40">
                                Max {riderStatus.count}/{riderStatus.max} heat
                              </span>
                            ) : null}
                            {!isAtOrOverMax && isNearMax ? (
                              <span className="native-v3-heat-meta-pill inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground ring-1 ring-border">
                                {riderStatus.count}/{riderStatus.max} heat
                              </span>
                            ) : null}
                          </div>
                        )}
                      </div>

                      {/* Placering + Status */}
                      <div
                        data-native-onboarding-target="heat-result-controls"
                        className={cn(
                          "native-v3-heat-result-controls flex items-center gap-2 transition-opacity",
                          rrActive && "opacity-45"
                        )}
                      >
                        <div className="native-v3-heat-points inline-flex w-1/2 overflow-hidden rounded-md border">
                          {resultOptions.map((option) => {
                            const isSelected = Number(gRes.position) === option.value;
                            const isPositionUnavailable = option.value > maxCompletedPosition;
                            return (
                              <button
                                key={option.value}
                                type="button"
                                disabled={rrActive || isSpecialStatus || isPositionUnavailable}
                                onClick={() =>
                                  setPositionExclusiveForGate(gate, option.value)
                                }

                                aria-pressed={isSelected}
                                title={
                                  isPositionUnavailable
                                    ? "Poängen är inte valbar när en eller flera förare redan har specialstatus."
                                    : rrActive
                                      ? "Bekräfta eller avbryt förarändringen innan du registrerar poäng."
                                    : undefined
                                }
                                className={[
                                  "h-8 flex-1 text-sm leading-none",
                                  "focus-visible:ring-2 focus-visible:ring-ring",
                                  isSelected ? "bg-primary text-white" : "hover:bg-secondary",
                                  isSpecialStatus || isPositionUnavailable ? "opacity-50 cursor-not-allowed" : "",
                                ].join(" ")}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                          <Select
                            value={normalizeHeatStatus(gRes.status)}
                            disabled={rrActive}
                            onValueChange={(value) => setStatusForGate(gate, value)}
                          >
                          <SelectTrigger
                            className={cn(
                              "native-v3-heat-status-trigger h-8 w-1/2 px-2 text-xs font-medium",
                              isSpecialStatus
                                ? "border-red-300 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200"
                                : "border-border bg-background text-muted-foreground"
                            )}
                          >
                            <SelectValue placeholder="Välj status" />
                          </SelectTrigger>
                          <SelectContent className="native-v3-heat-dialog-select max-h-72 bg-secondary text-xs">
                            {HEAT_STATUS_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value} className="text-xs">
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            <div id="heatDialogActions" className="native-v3-heat-dialog-actions flex justify-center gap-2 mt-4">
              {rrActive ? (
                <>
                  <Button variant="outline" onClick={handleCancelManualUnlockChanges}>
                    Avbryt
                  </Button>
                  <Button
                    onClick={handleConfirmManualUnlockChanges}
                    disabled={!canConfirmManualUnlockChanges}
                    title={!canConfirmManualUnlockChanges ? "Gör ett förarbyte för att bekräfta ändringen." : undefined}
                  >
                    Bekräfta
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Avbryt
                  </Button>
                  <Button
                    onClick={() => {
                      requestSave();
                      hap.tap();
                    }}
                    disabled={isSaving || !dialogComplete || nothingChanged || heatTimeHasError}
                    title={
                      isSaving
                        ? "Sparar resultatet."
                        : heatTimeHasError
                        ? "Ange heattiden i formatet 58.43 eller 1:02.11."
                        : !dialogComplete
                          ? "Fyll i unika placeringar 1–4 för alla som inte är uteslutna."
                        : nothingChanged
                          ? "Inga ändringar att spara."
                          : undefined
                    }
                  >
                    {isSaving ? "Sparar..." : "Spara resultat"}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}





